import { useState, useMemo, useCallback } from 'react';
import { LayoutGrid, Save, X, Plus, Lock, Search, Pencil } from 'lucide-react';
import { LayoutItem } from 'react-grid-layout';
import { useToast } from '@centraoneplus/ui';
import {
  useOrgTemplate,
  useOrgCatalog,
  useUpdateOrgTemplate,
  useAddOrgWidget,
  useRemoveOrgWidget,
  useUpdateOrgWidget,
} from '../../hooks/useOrgDashboardTemplates';
import { OrgDashboardTemplate, OrgWidgetCatalogItem, DashboardConfig } from '../../services/dashboards.service';
import { DashboardGrid } from '../../features/dashboard/DashboardGrid';
import { widgetDisplayTitle } from '../../features/dashboard/widgetTitles';
import { useDashboardMetrics, useAgentMetrics, useSlaMetrics, useTicketTrends, useRecentActivity } from '../../hooks/useAnalytics';

const ROLE_TABS = [
  { key: 'admin', label: 'Admin' },
  { key: 'supervisor', label: 'Supervisor' },
  { key: 'agent', label: 'Agent' },
] as const;

const PENDING_ID_PREFIX = 'pending_';

interface PendingWidget {
  id: string;
  widgetType: string;
  title: string;
  position: { x: number; y: number; w: number; h: number };
  config?: Record<string, any>;
}

// Chart type icons for the picker
const CHART_TYPE_LABELS: Record<string, { label: string; icon: string }> = {
  area: { label: 'Area', icon: '~' },
  line: { label: 'Line', icon: '/' },
  bar: { label: 'Bar', icon: '|' },
  donut: { label: 'Donut', icon: 'O' },
  pie: { label: 'Pie', icon: 'P' },
  'horizontal-bar': { label: 'H-Bar', icon: '=' },
  gauge: { label: 'Gauge', icon: 'G' },
  list: { label: 'List', icon: 'L' },
  custom: { label: 'Custom', icon: 'C' },
};

// Convert OrgDashboardTemplate to DashboardConfig for DashboardGrid
function toGridDashboard(
  template: OrgDashboardTemplate,
  pendingAdds: PendingWidget[],
  pendingRemoveIds: Set<string>,
  pendingConfigChanges?: Record<string, Record<string, any>>,
): DashboardConfig {
  const existingWidgets = template.widgets
    .filter(w => w.isEnabled && !pendingRemoveIds.has(w.id))
    .map(w => ({
      id: w.id,
      tenantId: w.tenantId,
      dashboardId: w.templateId,
      widgetType: w.widgetType,
      title: w.title,
      config: pendingConfigChanges?.[w.id] ? { ...w.config, ...pendingConfigChanges[w.id] } : w.config,
      position: w.position,
      isVisible: true,
      createdAt: w.createdAt,
      updatedAt: w.updatedAt,
    }));

  const pendingWidgets = pendingAdds.map(w => ({
    id: w.id,
    tenantId: template.tenantId,
    dashboardId: template.id,
    widgetType: w.widgetType,
    title: w.title,
    config: w.config || {},
    position: w.position,
    isVisible: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }));

  return {
    id: template.id,
    tenantId: template.tenantId,
    userId: '',
    name: template.name,
    roleType: template.roleType,
    isDefault: false,
    isOrgDefault: true,
    layout: template.layout,
    settings: template.settings,
    widgets: [...existingWidgets, ...pendingWidgets],
    createdAt: template.createdAt,
    updatedAt: template.updatedAt,
  };
}

export default function DashboardManagement() {
  const toast = useToast();
  const [activeRole, setActiveRole] = useState<string>('admin');
  const [isEditMode, setIsEditMode] = useState(false);
  const [pendingLayout, setPendingLayout] = useState<LayoutItem[] | null>(null);
  const [pendingAdds, setPendingAdds] = useState<PendingWidget[]>([]);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<Set<string>>(new Set());
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // Chart type selection state for catalog items (widgetType -> selected chartType)
  const [catalogChartTypes, setCatalogChartTypes] = useState<Record<string, string>>({});
  // Pending config changes for existing widgets (widgetId -> config updates)
  const [pendingConfigChanges, setPendingConfigChanges] = useState<Record<string, Record<string, any>>>({});

  const { data: template, isLoading: templateLoading } = useOrgTemplate(activeRole);
  const { data: catalog } = useOrgCatalog(activeRole);
  const updateTemplate = useUpdateOrgTemplate();
  const addWidgetMut = useAddOrgWidget();
  const removeWidgetMut = useRemoveOrgWidget();
  const updateWidgetMut = useUpdateOrgWidget();

  // Fetch live analytics data for the editor preview
  const dateRange = useMemo(() => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return { start: start.toISOString().split('T')[0], end: now.toISOString().split('T')[0] };
  }, []);
  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics(dateRange);
  const { data: agentData } = useAgentMetrics(dateRange);
  const { data: slaData } = useSlaMetrics(dateRange);
  const { data: trendsData } = useTicketTrends('week', dateRange);
  const { data: recentActivityData } = useRecentActivity();

  const activeWidgetTypes = useMemo(() => {
    const existing = (template?.widgets || [])
      .filter(w => w.isEnabled && !pendingRemoveIds.has(w.id))
      .map(w => w.widgetType);
    const added = pendingAdds.map(w => w.widgetType);
    return new Set([...existing, ...added]);
  }, [template?.widgets, pendingRemoveIds, pendingAdds]);

  const availableWidgets = useMemo(() => {
    if (!catalog) return [];
    return catalog.filter(c => !activeWidgetTypes.has(c.type) && c.type !== 'custom_metric');
  }, [catalog, activeWidgetTypes]);

  const filteredAvailable = useMemo(() => {
    if (!searchTerm) return availableWidgets;
    const lower = searchTerm.toLowerCase();
    return availableWidgets.filter(
      w => w.name.toLowerCase().includes(lower) || w.description.toLowerCase().includes(lower),
    );
  }, [availableWidgets, searchTerm]);

  const hasChanges = pendingLayout !== null || pendingAdds.length > 0 || pendingRemoveIds.size > 0 || Object.keys(pendingConfigChanges).length > 0;

  const handleAddWidget = useCallback(
    (catalogItem: OrgWidgetCatalogItem) => {
      const selectedChartType = catalogChartTypes[catalogItem.type] || catalogItem.defaultChartType;
      // Compute the next available row so the new widget lands immediately below
      // existing widgets, instead of using a y=9999 sentinel that would be persisted
      // verbatim and leave a huge gap on the read-only dashboard.
      const existingPositions: { x: number; y: number; w: number; h: number }[] = [
        ...((template?.widgets || [])
          .filter(w => w.isEnabled && !pendingRemoveIds.has(w.id))
          .map(w => w.position as { x: number; y: number; w: number; h: number })),
        ...pendingAdds.map(w => w.position),
      ];
      const nextY = existingPositions.length === 0
        ? 0
        : Math.max(...existingPositions.map(p => (p?.y ?? 0) + (p?.h ?? 0)));
      const newWidget: PendingWidget = {
        id: `${PENDING_ID_PREFIX}${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        widgetType: catalogItem.type,
        title: catalogItem.name,
        position: { x: 0, y: nextY, w: catalogItem.defaultSize.w, h: catalogItem.defaultSize.h },
        config: selectedChartType ? { chartType: selectedChartType } : {},
      };
      setPendingAdds(prev => [...prev, newWidget]);
      toast.success('Widget added', `${catalogItem.name} added. Click "Save" to apply.`);
    },
    [toast, catalogChartTypes, template?.widgets, pendingRemoveIds, pendingAdds],
  );

  const handleRemoveWidget = useCallback(
    (widgetId: string) => {
      const widget = template?.widgets.find(w => w.id === widgetId);
      if (widget?.isMandatory) {
        toast.warning('Cannot remove', 'This widget is mandatory and cannot be removed.');
        return;
      }
      if (widgetId.startsWith(PENDING_ID_PREFIX)) {
        setPendingAdds(prev => prev.filter(w => w.id !== widgetId));
        return;
      }
      setPendingRemoveIds(prev => new Set(prev).add(widgetId));
    },
    [template?.widgets, toast],
  );

  const handleChartTypeChange = useCallback((widgetId: string, chartType: string) => {
    // For pending adds, update the pending widget config
    if (widgetId.startsWith(PENDING_ID_PREFIX)) {
      setPendingAdds(prev => prev.map(w =>
        w.id === widgetId ? { ...w, config: { ...w.config, chartType } } : w,
      ));
      return;
    }
    // For existing widgets, store in pendingConfigChanges
    setPendingConfigChanges(prev => ({
      ...prev,
      [widgetId]: { ...prev[widgetId], chartType },
    }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!template) return;
    setIsSaving(true);
    try {
      for (const widgetId of pendingRemoveIds) {
        await removeWidgetMut.mutateAsync({ roleType: activeRole, widgetId });
      }
      // Map pending temp IDs -> real server IDs so we can stitch them back into pendingLayout.
      const pendingIdToRealId = new Map<string, string>();
      for (const pw of pendingAdds) {
        // If the user dragged the widget after adding it, prefer the layout's position.
        const layoutEntry = pendingLayout?.find(l => l.i === pw.id);
        const finalPosition = layoutEntry
          ? { x: layoutEntry.x, y: layoutEntry.y, w: layoutEntry.w, h: layoutEntry.h }
          : pw.position;
        const created = await addWidgetMut.mutateAsync({
          roleType: activeRole,
          widgetType: pw.widgetType,
          title: pw.title,
          config: pw.config || {},
          position: finalPosition,
        });
        // The mutation returns the full template (see org-dashboard-templates.service.ts addWidget).
        // Locate the newly-created widget by widgetType + position to obtain its real ID.
        const createdWidget = (created as any)?.widgets?.find?.(
          (w: any) =>
            w.widgetType === pw.widgetType &&
            (w.position?.x ?? 0) === finalPosition.x &&
            (w.position?.y ?? 0) === finalPosition.y,
        );
        if (createdWidget?.id) pendingIdToRealId.set(pw.id, createdWidget.id);
      }
      // Save config changes for existing widgets
      for (const [widgetId, configUpdate] of Object.entries(pendingConfigChanges)) {
        const existing = template.widgets.find(w => w.id === widgetId);
        if (existing) {
          await updateWidgetMut.mutateAsync({
            roleType: activeRole,
            widgetId,
            config: { ...existing.config, ...configUpdate },
          });
        }
      }
      if (pendingLayout) {
        // Translate pending IDs to real server IDs so the layout update covers
        // newly-added widgets too (otherwise their position would never be persisted).
        const validLayout = pendingLayout
          .map(l => {
            if (!l.i.startsWith(PENDING_ID_PREFIX)) {
              return { widgetId: l.i, x: l.x, y: l.y, w: l.w, h: l.h };
            }
            const realId = pendingIdToRealId.get(l.i);
            return realId ? { widgetId: realId, x: l.x, y: l.y, w: l.w, h: l.h } : null;
          })
          .filter((l): l is { widgetId: string; x: number; y: number; w: number; h: number } => l !== null);
        if (validLayout.length > 0) {
          await updateTemplate.mutateAsync({ roleType: activeRole, layout: validLayout });
        }
      }
      setPendingLayout(null);
      setPendingAdds([]);
      setPendingRemoveIds(new Set());
      setPendingConfigChanges({});
      setCatalogChartTypes({});
      setIsEditMode(false);
      setCatalogOpen(false);
      setSearchTerm('');
      toast.success('Dashboard saved', 'All changes have been applied.');
    } catch {
      toast.error('Save failed', 'Could not save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [template, pendingRemoveIds, pendingAdds, pendingLayout, pendingConfigChanges, activeRole, removeWidgetMut, addWidgetMut, updateWidgetMut, updateTemplate, toast]);

  const handleCancel = useCallback(() => {
    setPendingLayout(null);
    setPendingAdds([]);
    setPendingRemoveIds(new Set());
    setPendingConfigChanges({});
    setCatalogChartTypes({});
    setIsEditMode(false);
    setCatalogOpen(false);
    setSearchTerm('');
  }, []);

  const buildBaselineLayout = useCallback(() => {
    const items: { i: string; x: number; y: number; w: number; h: number }[] = [];
    (template?.widgets || [])
      .filter(w => w.isEnabled && !pendingRemoveIds.has(w.id))
      .forEach(w => {
        const pos = w.position as { x: number; y: number; w: number; h: number };
        items.push({ i: w.id, x: pos.x, y: pos.y, w: pos.w, h: pos.h });
      });
    pendingAdds.forEach(w => {
      items.push({ i: w.id, x: w.position.x, y: w.position.y, w: w.position.w, h: w.position.h });
    });
    return items;
  }, [template?.widgets, pendingRemoveIds, pendingAdds]);

  const handleLayoutChange = useCallback((layout: LayoutItem[]) => {
    const baseline = buildBaselineLayout();
    const baselineMap = new Map(baseline.map(b => [b.i, b]));
    const isRealChange =
      layout.length !== baseline.length ||
      layout.some(l => {
        const b = baselineMap.get(l.i);
        return !b || b.x !== l.x || b.y !== l.y || b.w !== l.w || b.h !== l.h;
      });
    setPendingLayout(isRealChange ? layout : null);
  }, [buildBaselineLayout]);

  const handleRoleChange = useCallback((role: string) => {
    if (isEditMode && hasChanges) {
      if (!confirm('You have unsaved changes. Discard them?')) return;
    }
    setActiveRole(role);
    handleCancel();
  }, [isEditMode, hasChanges, handleCancel]);

  // Build chart types map from catalog for inline pickers
  const chartTypesMap = useMemo(() => {
    if (!catalog) return {};
    const map: Record<string, string[]> = {};
    for (const item of catalog) {
      if (item.chartTypes && item.chartTypes.length > 1) {
        map[item.type] = item.chartTypes;
      }
    }
    return map;
  }, [catalog]);

  const gridDashboard = useMemo(
    () => template ? toGridDashboard(template, pendingAdds, pendingRemoveIds, pendingConfigChanges) : null,
    [template, pendingAdds, pendingRemoveIds, pendingConfigChanges],
  );

  const activeServerWidgets = useMemo(
    () => (template?.widgets || []).filter(w => w.isEnabled),
    [template?.widgets],
  );

  if (templateLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ==================== Full-screen editor overlay ====================
  if (isEditMode) {
    return (
      <div className="fixed inset-0 z-50 bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden">
        {/* Overlay header */}
        <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <LayoutGrid className="w-5 h-5" />
                Editing {ROLE_TABS.find(t => t.key === activeRole)?.label} Dashboard
              </h2>
              {/* Role tabs in overlay */}
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
                {ROLE_TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => handleRoleChange(tab.key)}
                    disabled={isSaving}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      activeRole === tab.key
                        ? 'bg-white dark:bg-gray-600 text-primary-700 dark:text-primary-300 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {hasChanges && (
                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Unsaved changes</span>
              )}
              <button
                onClick={() => setCatalogOpen(!catalogOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Widget
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                <Save className="w-4 h-4" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </div>
          </div>
        </div>

        {/* Overlay body */}
        <div className="flex-1 relative overflow-auto">
          {/* Widget catalog sidebar -- absolute overlay so it doesn't shrink the grid */}
          {catalogOpen && (
            <div className="absolute left-0 top-0 bottom-0 w-72 z-20 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto shadow-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Available Widgets</h3>
                <button onClick={() => setCatalogOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              {filteredAvailable.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                  {searchTerm ? 'No match' : 'All widgets added'}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredAvailable.map(item => (
                    <div key={item.type} className="rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 transition-colors">
                      <button
                        onClick={() => handleAddWidget(item)}
                        className="w-full flex items-start gap-3 p-3 text-left hover:bg-primary-50 dark:hover:bg-primary-900/10 rounded-t-lg transition-colors"
                      >
                        <Plus className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{item.name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{item.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {item.recommendedSize && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400">
                                Best: {item.recommendedSize.w}x{item.recommendedSize.h}
                              </span>
                            )}
                            {item.isMandatory && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400">
                                Required
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                      {/* Chart type selector for chart widgets */}
                      {item.chartTypes && item.chartTypes.length > 1 && (
                        <div className="px-3 pb-2 flex items-center gap-1 border-t border-gray-100 dark:border-gray-700 pt-2">
                          <span className="text-[10px] text-gray-400 mr-1 dark:text-gray-500">Chart:</span>
                          {item.chartTypes.map(ct => {
                            const selected = (catalogChartTypes[item.type] || item.defaultChartType) === ct;
                            return (
                              <button
                                key={ct}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCatalogChartTypes(prev => ({ ...prev, [item.type]: ct }));
                                }}
                                className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                                  selected
                                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                              >
                                {CHART_TYPE_LABELS[ct]?.label || ct}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Full-width grid editor */}
          <div className="flex-1 overflow-y-auto p-6">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Drag widgets to reposition and resize. Click the gear icon to change chart type.
            </p>

            {gridDashboard && gridDashboard.widgets.length > 0 ? (
              <DashboardGrid
                dashboard={gridDashboard}
                dashboardData={metrics}
                agentData={agentData}
                slaData={slaData}
                trendsData={trendsData}
                recentActivityData={recentActivityData}
                loading={metricsLoading}
                dateRange={dateRange}
                isEditMode={true}
                onLayoutChange={handleLayoutChange}
                onRemoveWidget={handleRemoveWidget}
                chartTypesMap={chartTypesMap}
                onChartTypeChange={handleChartTypeChange}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400 dark:text-gray-500">
                <LayoutGrid className="w-10 h-10 mb-3" />
                <p className="text-lg font-medium">No widgets yet</p>
                <p className="text-sm mt-1">Click "Add Widget" to get started</p>
              </div>
            )}
          </div>

        </div>
      </div>
    );
  }

  // ==================== Settings page (non-edit mode) ====================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <LayoutGrid className="w-5 h-5" />
          Dashboard Management
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Configure which widgets appear on the dashboard for each role. Changes apply to all users with the selected role.
        </p>
      </div>

      {/* Role Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
        {ROLE_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => handleRoleChange(tab.key)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeRole === tab.key
                ? 'bg-white dark:bg-gray-700 text-primary-700 dark:text-primary-300 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Customize button */}
      <button
        onClick={() => setIsEditMode(true)}
        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
      >
        <Pencil className="w-4 h-4" />
        Customize {ROLE_TABS.find(t => t.key === activeRole)?.label} Dashboard
      </button>

      {/* Active Widgets Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Active Widgets ({activeServerWidgets.length})
          </h3>
        </div>
        {activeServerWidgets.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No widgets configured. Click "Customize" to add widgets.
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {activeServerWidgets.map(widget => (
              <div key={widget.id} className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  {/* The slot is always there so titles share one left edge. */}
                  <span className="w-4 flex-shrink-0" title={widget.isMandatory ? 'Mandatory widget' : undefined}>
                    {widget.isMandatory && <Lock className="w-4 h-4 text-amber-500" />}
                  </span>
                  <div>
                    {/* Stored titles can still hold a retired catalog name, so
                        the row is labelled through the same normalizer the
                        rendered card uses — otherwise this list and the widget
                        heading disagree. */}
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {widgetDisplayTitle(widget.widgetType, widget.title)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="order-last text-xs text-gray-500 dark:text-gray-400">
                    {(widget.position as any)?.w || '?'}x{(widget.position as any)?.h || '?'}
                  </span>
                  {widget.isMandatory && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400">
                      Required
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Info note */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 text-sm text-blue-700 dark:text-blue-300">
        <strong>Note:</strong> Dashboard changes apply to all users with the <strong>{activeRole}</strong> role across your organization.
        Widgets marked with a lock icon are mandatory and always visible.
      </div>
    </div>
  );
}